import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";

const ThemeContext = createContext();

export function useTheme() {
    return useContext(ThemeContext);
}

export function ThemeProvider({ children }) {
    const colors = useMemo(() => {
        return {
            light: {
                background: "bg-gray-50",
                colors: {
                    yellow: {
                        primary: "text-yellow-500",
                        secondary: "text-gray-800",
                        bgPrimary: "bg-yellow-500",
                        bgSecondary: "bg-gray-800",
                        borderPrimary: "border-yellow-500",
                        borderSecondary: "border-gray-800",
                        borders: {
                            primary: "#FDE047",
                            secondary: "#000",
                        },
                    },
                    green: {
                        primary: "text-green-500",
                        secondary: "text-gray-800",
                        bgPrimary: "bg-green-500",
                        bgSecondary: "bg-gray-800",
                        borderPrimary: "border-green-500",
                        borderSecondary: "border-gray-800",
                        borders: {
                            primary: "#86EFAC",
                            secondary: "#000",
                        },
                    },
                    red: {
                        primary: "text-red-500",
                        secondary: "text-gray-800",
                        bgPrimary: "bg-red-500",
                        bgSecondary: "bg-gray-800",
                        borderPrimary: "border-red-500",
                        borderSecondary: "border-gray-800",
                        borders: {
                            primary: "#FCA5A5",
                            secondary: "#000",
                        },
                    },
                },
            },
            dark: {
                background: "bg-gray-800",
                colors: {
                    yellow: {
                        primary: "text-yellow-300",
                        secondary: "text-white",
                        bgPrimary: "bg-yellow-300",
                        bgSecondary: "bg-white",
                        borderPrimary: "border-yellow-300",
                        borderSecondary: "border-white",
                        borders: {
                            primary: "#FDE047",
                            secondary: "#fff",
                        },
                    },
                    green: {
                        primary: "text-green-300",
                        secondary: "text-white",
                        bgPrimary: "bg-green-300",
                        bgSecondary: "bg-white",
                        borderPrimary: "border-green-300",
                        borderSecondary: "border-white",
                        borders: {
                            primary: "#86EFAC",
                            secondary: "#fff",
                        },
                    },
                    red: {
                        primary: "text-red-300",
                        secondary: "text-white",
                        bgPrimary: "bg-red-300",
                        bgSecondary: "bg-white",
                        borderPrimary: "border-red-300",
                        borderSecondary: "border-white",
                        borders: {
                            primary: "rgb(252 165 165)",
                            secondary: "#fff",
                        },
                    },
                },
            },
        };
    }, []);

    const [theme, setTheme] = useState(() => {
        const theme = localStorage.getItem("theme");
        if (!theme || !["light", "dark"].includes(theme)) {
            localStorage.setItem("theme", "dark");
        }

        return localStorage.getItem("theme");
    });
    const [selectedColor, setSelectedColor] = useState(() => {
        const color = localStorage.getItem("color");
        if (!color || !["yellow", "green", "red"].includes(color)) {
            localStorage.setItem("color", "yellow");
        }

        return localStorage.getItem("color");
    });
    const [primaryColor, setPrimaryColor] = useState("");
    const [secondaryColor, setSecondaryColor] = useState("");
    const [backgroundColor, setBackgroundColor] = useState("");
    const [chooseColor, setChooseColor] = useState(false);
    const [border, setBorder] = useState("");
    const [showSidebar, setShowSidebar] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        if (theme === "dark") {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }

        setPrimaryColor(colors[theme].colors[selectedColor].primary);
        setSecondaryColor(colors[theme].colors[selectedColor].secondary);
        setBackgroundColor(colors[theme].background);
        setBorder(
            `${colors[theme].colors[selectedColor].borders.primary} transparent ${colors[theme].colors[selectedColor].borders.secondary} transparent`
        );

        const handleKeyboardEvent = (event) => {
            switch (event.keyCode) {
                case 49:
                case 97:
                    navigate("/", { replace: true });
                    break;
                case 50:
                case 98:
                    navigate("/about", { replace: true });
                    break;
                case 51:
                case 99:
                    navigate("/experience", { replace: true });
                    break;
                case 52:
                case 100:
                    navigate("/works", { replace: true });
                    break;
                default:
                    break;
            }
        };

        document.addEventListener("keydown", handleKeyboardEvent, true);
        return () => {
            document.removeEventListener("keydown", handleKeyboardEvent, true);
        };
    }, [theme, colors, selectedColor, navigate]);

    const handleThemeChange = (theme) => {
        if (localStorage.getItem("theme") !== theme) {
            localStorage.setItem("theme", theme);
        }

        setTheme(theme);
    };

    const handleColorChange = (color) => {
        if (localStorage.getItem("color") !== color) {
            localStorage.setItem("color", color);
        }

        setSelectedColor(color);
    };

    let value = {
        primaryColor,
        setPrimaryColor,
        secondaryColor,
        setSecondaryColor,
        theme,
        handleThemeChange,
        handleColorChange,
        backgroundColor,
        chooseColor,
        setChooseColor,
        colors,
        border,
        selectedColor,
        showSidebar,
        setShowSidebar,
    };

    return (
        <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
    );
}
